import React, { createContext, useContext, useState } from 'react';
import { checkUserDetailsFromCookie } from '../../../../api_calls/userManagement';
import { useDispatch, useSelector } from 'react-redux';
import { deleteItems, updateStateData } from '../../../../Store/actions';
import { isValidFileName } from '../../../../api_calls/utils';
import { setBatchLoadingStatus } from '../../../../api_calls/requestConfig';

const FileContext = createContext(null);

export const FileProvider = ({ children }) => {
    const dispatchAction = useDispatch();
    const userStateDetails = useSelector((state) => state.userSelections);
    const [acceptedFiles, setAcceptedFiles] = useState([]);
    const [duplicateFiles, setDuplicateFiles] = useState([]);
    const [rejectedFiles, setRejectedFiles] = useState([]);
    const [invalidFiles, setInvalidFiles] = React.useState([]);
    const [longDurationRejectedFiles, setLongDurationRejectedFiles] = useState([]);
    const [maxFilesReached, setMaxFilesReached] = useState([]);
    const [resolutionRejectedFiles, setResolutionRejectedFiles] = useState([]);
    const checkUserDetails = checkUserDetailsFromCookie();
    let userDetailObj = {};
    if (checkUserDetails.success === true) {
        userDetailObj = checkUserDetails.user_detail;
    }

    const maxFileSize = 50 * 1024 * 1024;
    const minFileInBytes = 6;

    const checkFileNameValidation = (newFiles) => {
        let discardedFiles = [];
        const validFile = [...invalidFiles, ...newFiles].filter((file) => {
            const isValid = isValidFileName(file.name);
            if (!isValid) {
                discardedFiles.push(file);
            }
            return isValid;
        });

        setInvalidFiles([...discardedFiles]);

        return validFile.length !== 0 ? true : false;
    };

    const addFiles = async (files = [], rejections = []) => {
        const newDuplicates = [];
        const newRejected = [...rejections.map(r => r.file)];
        const newAccepted = [];
        const newResolutionRejected = [];
        const newlongDurationRejected = [];
        const newMaxFilesReached = [];
        const fileUploadLimit = userStateDetails.new_loop_data.visual_upload_limit;
        const uploadedFileDuration = userStateDetails.new_loop_data.visual_upload_duration;

        checkFileNameValidation(files);

        const durationCheckPromises = files.map(file => {

            // files already uploaded and save
            const preUploadedActiveVisuals = typeof userStateDetails.loop_campaign_visuals !== "undefined" && userStateDetails.loop_campaign_visuals.length !== 0
                ? userStateDetails.loop_campaign_visuals.filter(visual => visual.active)
                : [];

            // Dateinamen der aktiven Visuals für Duplikatprüfung
            const preUploadedFileNames = preUploadedActiveVisuals.map(visual => visual.content);

            // newly uploaded files
            const newUploads = acceptedFiles.map(acceptedFileObj => acceptedFileObj.name);

            // check duplicate
            const isDuplicate = [...preUploadedFileNames, ...newUploads].some(fileName => fileName === file.name);
            if (isDuplicate) {
                newDuplicates.push(file);
                return Promise.resolve(); // skip
            }

            if (file.type === "video/mp4") {
                return new Promise(resolve => {
                    const objectUrl = URL.createObjectURL(file);
                    const video = document.createElement("video");
                    video.preload = "metadata";
                    video.src = objectUrl;

                    video.onloadedmetadata = () => {
                        URL.revokeObjectURL(objectUrl);
                        const totalAcceptedCount = acceptedFiles.length + newAccepted.length + preUploadedActiveVisuals.length;

                        // Check resolution: reject if width>1920 or height>1080
                        const width = video.videoWidth;
                        const height = video.videoHeight;
                        if (width > 1920 || height > 1920) {
                            newResolutionRejected.push(file);
                            resolve();
                            return;
                        }

                        if (video.duration < 1 || video.duration > parseInt(uploadedFileDuration + 1)) {
                            newlongDurationRejected.push(file);
                        } else if (totalAcceptedCount >= fileUploadLimit) {
                            newMaxFilesReached.push(file);
                        } else {
                            newAccepted.push(file);
                        }

                        resolve();
                    };

                    video.onerror = () => {
                        newResolutionRejected.push(file);
                        resolve();
                    };
                });
            } else {
                const totalAcceptedCount = acceptedFiles.length + newAccepted.length + preUploadedActiveVisuals.length;

                if (totalAcceptedCount >= fileUploadLimit) {
                    newMaxFilesReached.push(file);
                } else {
                    newAccepted.push(file);
                }
                return Promise.resolve();
            }
        });

        setBatchLoadingStatus(true)
        await Promise.all(durationCheckPromises).then(() => setBatchLoadingStatus(false));

        setAcceptedFiles(prev => {
            const uniqueFiles = newAccepted.filter(
                (file) => isValidFileName(file.name)
            );
            let newlyAccepted = [...prev, ...uniqueFiles];
            dispatchAction(updateStateData({
                accepted_loop_files_length: newlyAccepted.length,
            }));
            return newlyAccepted;
        });
        setDuplicateFiles(prev => [...prev, ...newDuplicates]);
        setRejectedFiles(prev => [...prev, ...newRejected]);
        setResolutionRejectedFiles(prev => [...prev, ...newResolutionRejected]);
        setLongDurationRejectedFiles(prev => [...prev, ...newlongDurationRejected]);
        setMaxFilesReached(prev => [...prev, ...newMaxFilesReached]);
    };


    const removeFile = (fileName) => {
        // setAcceptedFiles(prev => prev.filter(f => f.name !== fileName));
        setAcceptedFiles(prev => {
            let filteredList = prev.filter(f => f.name !== fileName);
            dispatchAction(updateStateData({
                accepted_loop_files_length: filteredList.length,
            }));
            return filteredList;
        });
    };

    const clearFiles = () => {
        setAcceptedFiles([]);
        setDuplicateFiles([]);
        setRejectedFiles([]);
        setLongDurationRejectedFiles([]);
        setMaxFilesReached([]);
        setInvalidFiles([]);
        setResolutionRejectedFiles([]);
        dispatchAction(deleteItems(["accepted_loop_files_length"]));
    };

    return (
        <FileContext.Provider
            value={{
                acceptedFiles,
                duplicateFiles,
                rejectedFiles,
                longDurationRejectedFiles,
                maxFilesReached,
                invalidFiles,
                resolutionRejectedFiles,
                addFiles,
                removeFile,
                clearFiles
            }}
        >
            {children}
        </FileContext.Provider>
    );
};

export const useFileStore = () => {
    const context = useContext(FileContext);
    if (!context) {
        throw new Error('useFileStore must be used within a FileProvider');
    }
    return context;
};